import get from 'lodash.get';
import keyBy from 'lodash.keyby';
import orderBy from 'lodash.orderby';
import { format } from 'date-fns';
import createDeepEqualSelector from 'utils/reselect/typescript/createDeepEqualSelector';
import { AppState } from 'services/types';
// @ts-ignore
import { selectPeopleKeyedByUserId } from 'services/people/selectors';
import {
    selectFirstDate,
    selectLastDate
    // @ts-ignore
} from 'components/DatePagination/selectors';
import { makeSelectQueryStringValue } from 'workspaces/react-router-selectors';
import { PayrollExportsState } from './types';

const selectPayrollExports = (state: AppState): PayrollExportsState =>
    state.payrollExports;

const selectPayrollExportId = (
    state: AppState,
    props: { id: number }
): number => props.id;

const selectFormattedPayrollExports = createDeepEqualSelector(
    selectPayrollExports,
    selectPeopleKeyedByUserId,
    (payrollExports, peopleKeyedByUserId) => {
        return payrollExports.map((payrollExport) => {
            const fullName = get(
                peopleKeyedByUserId,
                [payrollExport.createdByUserId, 'fullName'],
                payrollExport.email
            );

            return {
                ...payrollExport,
                createdAt: format(
                    new Date(payrollExport.createdAt),
                    'yyyy-MM-dd'
                ),
                createdBy: fullName
            };
        });
    }
);

const selectFilteredPayrollExports = createDeepEqualSelector(
    selectFormattedPayrollExports,
    selectFirstDate,
    selectLastDate,
    (payrollExports, firstDate: string, lastDate: string) =>
        payrollExports.filter(
            ({ createdAt }) => createdAt >= firstDate && createdAt <= lastDate
        )
);

const selectOrderedData = createDeepEqualSelector(
    [selectFilteredPayrollExports, makeSelectQueryStringValue('sort')],
    (payrollExports, sortState: any = 'createdAt_ascending') => {
        const attributes = sortState.split('_');
        const propertyName = attributes[0];
        const order = attributes[1] !== 'descending' ? 'asc' : 'desc';
        return orderBy(payrollExports, propertyName, order);
    }
);

const selectPayrollExportsKeyedById = createDeepEqualSelector(
    selectOrderedData,
    (payrollExports) => keyBy(payrollExports, 'id')
);

const selectPayrollExportsIds = createDeepEqualSelector(
    selectOrderedData,
    (payrollExports) => payrollExports.map((payrollExport) => payrollExport.id)
);

const selectNumberOfTimesheets = createDeepEqualSelector(
    selectPayrollExportId,
    selectPayrollExportsKeyedById,
    (payrollExportId, payrollExportsKeyedById) =>
        get(payrollExportsKeyedById, [payrollExportId, 'numberOfTimesheets'])
);

const makeSelectNumberOfTimesheets = (): typeof selectNumberOfTimesheets =>
    selectNumberOfTimesheets;

export {
    selectPayrollExports,
    selectPayrollExportId,
    selectPayrollExportsIds,
    selectPayrollExportsKeyedById,
    selectOrderedData,
    selectFormattedPayrollExports,
    selectFilteredPayrollExports,
    selectNumberOfTimesheets,
    makeSelectNumberOfTimesheets
};
